"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Root = void 0;
var _styled = _interopRequireDefault(require("@emotion/styled"));
var _designTokensReact = require("@smartsheet/design-tokens-react");
var _createFocusRingStyles = require("../../../../util/createFocusRingStyles");
var _inputFieldBaseProps = require("../../../../util/inputFieldBaseProps");
var _withFocusClassName = require("../../../../util/withFocusClassName");
var _formInputTheme = require("../../../textinput/styled/formInputTheme");
var _selectV2Theme = require("../selectV2Theme");
const Root = (0, _withFocusClassName.withFocusToggleClassName)((0, _styled.default)("div")`
    position: relative;
    padding-right: var(--sds--selectv2--wrapper--padding-right, 0px);
    padding-top: var(--sds--selectv2--wrapper--padding-top, 0px);
    padding-left: var(--sds--selectv2--wrapper--padding-left, 0px);
    padding-bottom: var(--sds--selectv2--wrapper--padding-bottom, 0px);

    // To allow certain elements within here to use z-index without breaking stacking context.
    isolation: isolate;

    // Calling this out explicitly so people notice.
    /* box-sizing: content-box; */
    box-sizing: border-box;

    display: flex;
    flex-direction: row;
    align-items: center;
    min-height: ${_ref => {
  let {
    themeMode,
    inputFieldHeight
  } = _ref;
  return `var(--sds--form--height--${inputFieldHeight === 32 ? "default" : "large"}, ${_formInputTheme.formInputTheme.height[(0, _inputFieldBaseProps.getInputFieldHeightValue)(themeMode, inputFieldHeight)](themeMode)}px)`;
}};
    background-color: ${getBackgroundColor};
    color: ${(0, _designTokensReact.color)("color--text--neutral--strong")};

    border-radius: ${_ref2 => {
  let {
    themeMode
  } = _ref2;
  return `var(--sds--form--border-radius, ${_formInputTheme.formInputTheme.borderRadius(themeMode)})`;
}};

    ${_ref3 => {
  let {
    isFocused,
    omitFocusRing,
    themeMode
  } = _ref3;
  return isFocused && !omitFocusRing && _formInputTheme.formInputTheme.focusRing.enabled(themeMode) ? (0, _createFocusRingStyles.createFocusRingStyles)({
    theme: themeMode,
    gap: 1,
    // This is kinda weird, but by using an empty string here, we defer the show/hide
    // behavior to the ternary statement above.
    focusSelector: ``,
    componentBorderWidth: 0,
    componentRadius: `var(--sds--form--border-radius, ${_formInputTheme.formInputTheme.borderRadius(themeMode)})`
  }) : "";
}}

    // We're going to use a second ring here to fake the border. Doing this allows us to avoid a lot
    // of positioning issues due to the border width changes necessary for Ursa. Since there are a
    // number of elements that add some margin-top, left, top, etc., they all need to be adjusted
    // when validationState changes in Ursa. Lodestar doesn't have this problem. Using this method,
    // this "border" will overlay the outer edge of the SelectV2 and inner elements will be
    // positioned against the outer edge of the root element which now has no actual border.
    ${props => (0, _createFocusRingStyles.createFocusRingStyles)({
  theme: props.themeMode,
  gap: -_formInputTheme.formInputTheme.borderWidth[props.validationState](props.themeMode),
  // Always show this ring.
  focusSelector: ``,
  componentBorderWidth: 0,
  componentRadius: _formInputTheme.formInputTheme.borderRadius(props.themeMode),
  innerColorOverride: "transparent",
  colorOverride: getBorderColor(props),
  ringWidth: getBorderWidth(props),
  pseudoElement: "after"
})}

    ${props => props.appearance === "underlined" && `
        // specific styles for the underlined appearance's focus ring
        &::before {
            // Needed to ensure the focus ring and white offset ring cover the underline
            z-index: 1;
        }

        // specific styles for the underlined appearance's "border"
        &::after {
            // this overrides the border width set in createFocusRingStyles
            border-width: 0;
            border-radius: 0;
            ${getBorderRule(props)}-width: ${getBorderWidth(props)}px;

            ${props.isFocused ? `
                        // to hide the tiny triangle sliver of underline peeking out in Ursa
                        right: ${_selectV2Theme.selectV2Theme.border.focusedInset(props.themeMode)};
                        left: ${_selectV2Theme.selectV2Theme.border.focusedInset(props.themeMode)};
                        // For Lodestar flat underline but rounded focus ring
                        border-radius: ${_selectV2Theme.selectV2Theme.border.focusedRadius(props.themeMode)};
                    ` : ``}
        }
        `}
    
    ${props => props.appearance === "borderless" && (_formInputTheme.formInputTheme.focusRing.enabled(props.themeMode) || !props.isFocused) && `
            &::after {
                border: none;
            }
        `}
    
    ${_ref4 => {
  let {
    isFocused,
    themeMode,
    validationState,
    isReadonly
  } = _ref4;
  if (!isFocused && !isReadonly && validationState === "rest") {
    return `
                &:hover:after {
                    border-color: var(--sds--form--border-color--hover, ${_formInputTheme.formInputTheme.borderColor.hover(themeMode)});
                }
            `;
  }
}}

    ${_ref5 => {
  let {
    isReadonly
  } = _ref5;
  return isReadonly && `
                cursor: not-allowed;
            `;
}}
`);
exports.Root = Root;
function getBorderWidth(_ref6) {
  let {
    themeMode,
    validationState,
    isFocused,
    focusMode
  } = _ref6;
  if (validationState === "error") {
    return _formInputTheme.formInputTheme.borderWidth.error(themeMode);
  } else if (isFocused && focusMode !== "keyboard") {
    return _formInputTheme.formInputTheme.borderWidth.active(themeMode);
  } else {
    return _formInputTheme.formInputTheme.borderWidth.rest(themeMode);
  }
}
function getBackgroundColor(_ref7) {
  let {
    isReadonly,
    themeMode,
    validationState,
    appearance
  } = _ref7;
  if (appearance === "underlined") {
    return _formInputTheme.formInputTheme.backgroundColor.underlined(themeMode);
  }
  if (isReadonly) {
    return _formInputTheme.formInputTheme.backgroundColor.readonly(themeMode);
  } else if (validationState === "error") {
    return _formInputTheme.formInputTheme.backgroundColor.error(themeMode);
  } else {
    return _formInputTheme.formInputTheme.backgroundColor.rest(themeMode);
  }
}
function getBorderColor(_ref8) {
  let {
    isFocused,
    themeMode,
    validationState,
    isReadonly,
    focusMode
  } = _ref8;
  const isKeyboardFocused = focusMode === "keyboard";
  if (validationState === "error" && isFocused) {
    return `var(--sds--form--border-color--error, ${_formInputTheme.formInputTheme.borderColor.errorFocus(themeMode)})`;
  } else if (isReadonly) {
    return `var(--sds--form--border-color--disabled, ${_formInputTheme.formInputTheme.borderColor.readonly(themeMode)})`;
  } else if (isFocused && !isKeyboardFocused) {
    return `var(--sds--form--border-color--active, ${_formInputTheme.formInputTheme.borderColor.focus(themeMode)})`;
  } else if (isFocused && isKeyboardFocused) {
    return `var(--sds--form--border-color--rest, ${_formInputTheme.formInputTheme.borderColor.rest(themeMode)})`;
  } else if (validationState === "error") {
    return `var(--sds--form--border-color--error, ${_formInputTheme.formInputTheme.borderColor.error(themeMode)})`;
  } else if (isFocused) {
    return _formInputTheme.formInputTheme.borderColor.focus(themeMode);
  } else {
    return `var(--sds--form--border-color--rest, ${_formInputTheme.formInputTheme.borderColor.rest(themeMode)})`;
  }
}
function getBorderRule(_ref9) {
  let {
    appearance,
    isFocused,
    themeMode
  } = _ref9;
  return appearance === "underlined" && !isFocused ? "border-bottom" : appearance === "underlined" ? _selectV2Theme.selectV2Theme.border.focusedRule(themeMode) : "border";
}
//# sourceMappingURL=Root.js.map